import { t } from '@lingui/macro'
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { ContactAttemptOutcome } from '@om1/aspen-api/codegen/client/models/ContactAttemptOutcome'
import { ContactAttemptType } from '@om1/aspen-api/codegen/client/models/ContactAttemptType'
import { ContactLogResponse } from '@om1/aspen-api/codegen/client/models/ContactLogResponse'
import { EntityType, LogAttemptData } from '@om1/aspen-api/codegen/hooks/useContactAttempts'
import { dateTimeETToUTCISOString, utcISOStringToDateForETDisplay } from '@om1/platform-components'
import React, { useEffect, useState } from 'react'

// Single source of truth for contact types and outcomes — value + Lingui label in one place.
// To add a new option: add one entry here only (no parallel array or switch needed).
/* eslint-disable string-to-lingui/missing-lingui-transformation */
const CONTACT_TYPE_ENTRIES: { value: ContactAttemptType; label: () => string }[] = [
    { value: 'Phone', label: () => t`Phone` },
    { value: 'Text', label: () => t`Text` },
    { value: 'Email', label: () => t`Email` },
    { value: 'Other', label: () => t`Other` }
]

const ATTEMPT_OUTCOME_ENTRIES: { value: ContactAttemptOutcome; label: () => string }[] = [
    { value: 'No Answer/Voicemail Left' as ContactAttemptOutcome, label: () => t`No Answer/Voicemail Left` },
    {
        value: 'No Answer/Not Available – follow up call scheduled' as ContactAttemptOutcome,
        label: () => t`No Answer/Not Available – follow up call scheduled`
    },
    { value: 'Answered and Scheduled Consent Appointment' as ContactAttemptOutcome, label: () => t`Answered and Scheduled Consent Appointment` },
    { value: 'Answered and Completed Consent Review' as ContactAttemptOutcome, label: () => t`Answered and Completed Consent Review` },
    { value: 'Wrong Number' as ContactAttemptOutcome, label: () => t`Wrong Number` },
    { value: 'No Longer Interested in the Study' as ContactAttemptOutcome, label: () => t`No Longer Interested in the Study` },
    {
        value: 'Answered – eligibility pending / follow-up call scheduled' as ContactAttemptOutcome,
        label: () => t`Answered – eligibility pending / follow-up call scheduled`
    },
    {
        value: 'Undecided – patient will initiate next contact' as ContactAttemptOutcome,
        label: () => t`Undecided – patient will initiate next contact`
    }
]
/* eslint-enable string-to-lingui/missing-lingui-transformation */

const CONTACT_TYPES: ContactAttemptType[] = CONTACT_TYPE_ENTRIES.map((e) => e.value)
const ATTEMPT_OUTCOMES: ContactAttemptOutcome[] = ATTEMPT_OUTCOME_ENTRIES.map((e) => e.value)

export const getContactTypeDisplay = (type: ContactAttemptType): string => CONTACT_TYPE_ENTRIES.find((e) => e.value === type)?.label() ?? type

export const getAttemptOutcomeDisplay = (outcome: ContactAttemptOutcome): string =>
    ATTEMPT_OUTCOME_ENTRIES.find((e) => e.value === outcome)?.label() ?? outcome

export interface LogAttemptInitialValues {
    contactType?: ContactAttemptType
    outcome?: ContactAttemptOutcome
    notes?: string | null
    nextContactTime?: Date | null
}

interface LogAttemptInputProps {
    entityName: string
    onSubmit: (data: LogAttemptData) => void
    onCancel: () => void
    entityType?: EntityType
    entityId?: string
    contactAttempts: ContactLogResponse[]
    isLoading?: boolean
    error?: string | null
    /** Initial values for editing an existing contact attempt */
    initialValues?: LogAttemptInitialValues
}

export const LogAttemptInput: React.FC<LogAttemptInputProps> = ({
    entityName,
    onSubmit,
    onCancel,
    entityType,
    entityId,
    contactAttempts,
    isLoading = false,
    error: propError = null,
    initialValues
}) => {
    // Determine if we're in edit mode
    const isEditMode = !!initialValues

    // Initialize state with initial values (for edit) or defaults (for create)
    const [contactType, setContactType] = useState<ContactAttemptType>(initialValues?.contactType ?? CONTACT_TYPES[0])
    const [outcome, setOutcome] = useState<ContactAttemptOutcome>(initialValues?.outcome ?? ATTEMPT_OUTCOMES[0])
    const [notes, setNotes] = useState(initialValues?.notes ?? '')

    // Parse initial nextContactTime (UTC from backend) and show as ET in pickers
    const getInitialDateTime = (): Date | null => {
        const raw = initialValues?.nextContactTime
        if (!raw) return null
        const iso = typeof raw === 'string' ? raw : raw.toISOString()
        return utcISOStringToDateForETDisplay(iso)
    }

    const [selectedDate, setSelectedDate] = useState<Date | null>(getInitialDateTime())
    const [selectedTime, setSelectedTime] = useState<Date | null>(getInitialDateTime())
    const [nextContactTime, setNextContactTime] = useState<Date | null>(initialValues?.nextContactTime ?? null)
    const [dateError, setDateError] = useState<string>('')
    const [error, setError] = useState<string | null>(propError)
    // Track if the date was modified from initial value (to allow past dates in edit mode if unchanged)
    const [dateWasModified, setDateWasModified] = useState(false)

    useEffect(() => {
        setError(propError)
    }, [propError])

    useEffect(() => {
        if (selectedDate && selectedTime) {
            const newDate = new Date(selectedDate)
            newDate.setHours(selectedTime.getHours())
            newDate.setMinutes(selectedTime.getMinutes())
            setNextContactTime(newDate)
        } else {
            setNextContactTime(null)
        }
    }, [selectedDate, selectedTime])

    const handleDateChange = (newValue: Date | null) => {
        setSelectedDate(newValue)
        setDateError('')
        setDateWasModified(true)
    }

    const handleSubmit = () => {
        // Only validate past date if user changed the date (allow unchanged past dates in edit mode)
        // Compare in ET since next contact time is stored as ET
        const shouldValidateDate = !isEditMode || dateWasModified
        if (shouldValidateDate && selectedDate) {
            const etToday = utcISOStringToDateForETDisplay(new Date().toISOString())
            if (etToday) {
                const selectedYMD = selectedDate.getFullYear() * 10000 + selectedDate.getMonth() * 100 + selectedDate.getDate()
                const todayYMD = etToday.getFullYear() * 10000 + etToday.getMonth() * 100 + etToday.getDate()
                if (selectedYMD < todayYMD) {
                    setDateError(t`Cannot select a past date`)
                    return
                }
            }
        }

        // Treat selected date/time as Eastern Time and convert to UTC for storage
        let nextContactTimeUTC: Date | null = null
        if (nextContactTime) {
            const utcIso = dateTimeETToUTCISOString(
                nextContactTime.getFullYear(),
                nextContactTime.getMonth(),
                nextContactTime.getDate(),
                nextContactTime.getHours(),
                nextContactTime.getMinutes()
            )
            nextContactTimeUTC = new Date(utcIso)
        }

        onSubmit({
            contactType,
            outcome,
            notes,
            nextContactTime: nextContactTimeUTC
        })
    }

    const isToday = (date: Date | null) => {
        if (!date) return false
        const today = new Date()
        return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
    }

    return (
        <div className='bg-white rounded-lg p-6'>
            <h2 className='text-xl font-semibold mb-2'>{isEditMode ? t`Edit Contact Attempt` : t`Log Contact Attempt`}</h2>
            <p className='text-sm text-gray-600 mb-4'>{entityName}</p>

            {error && <div className='mb-4 p-2 bg-red-50 text-red-600 rounded'>{error}</div>}

            <div className='space-y-4'>
                <FormControl fullWidth variant='outlined'>
                    <InputLabel id='contact-type-label' sx={{ backgroundColor: 'white', px: 0.5 }}>{t`Contact Type`}</InputLabel>
                    <Select
                        labelId='contact-type-label'
                        value={contactType}
                        onChange={(e) => setContactType(e.target.value as ContactAttemptType)}
                        name='contact_type'
                        data-testid='contact-type-select'
                        label={t`Contact Type`}
                    >
                        {CONTACT_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                                {getContactTypeDisplay(type)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth variant='outlined'>
                    <InputLabel id='attempt-outcome-label' sx={{ backgroundColor: 'white', px: 0.5 }}>{t`Attempt Outcome`}</InputLabel>
                    <Select
                        labelId='attempt-outcome-label'
                        value={outcome}
                        onChange={(e) => setOutcome(e.target.value as ContactAttemptOutcome)}
                        name='attempt_outcome'
                        data-testid='attempt-outcome-select'
                        label={t`Attempt Outcome`}
                    >
                        {ATTEMPT_OUTCOMES.map((outcome) => (
                            <MenuItem key={outcome} value={outcome}>
                                {getAttemptOutcomeDisplay(outcome)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={t`Notes`}
                    name='notes'
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid='notes-input'
                    variant='outlined'
                />

                <div className='bg-gray-50 p-4 rounded-lg space-y-4'>
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>{t`Suggested Next Contact (Optional, Eastern Time)`}</label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                minDate={new Date()}
                                value={selectedDate}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        error: !!dateError,
                                        helperText: dateError,
                                        inputProps: {
                                            'data-testid': 'next-contact-date'
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>{t`Time (Optional, ET)`}</label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <TimePicker
                                minTime={isToday(selectedDate) ? new Date() : undefined}
                                value={selectedTime}
                                onChange={(newValue) => setSelectedTime(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                </div>

                <div className='flex justify-end space-x-2'>
                    <button
                        onClick={onCancel}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
                        aria-label={t`Cancel`}
                    >
                        {t`Cancel`}
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className='px-4 py-2 text-sm font-medium text-white bg-primary-main rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed'
                        aria-label={isLoading ? t`Saving...` : isEditMode ? t`Update` : t`Save`}
                    >
                        {isLoading ? t`Saving...` : isEditMode ? t`Update` : t`Save`}
                    </button>
                </div>
            </div>
        </div>
    )
}
